import React, { useState, useEffect } from 'react';

interface BrandAboutProps {
  data?: {
    heading?: string;
    subheading?: string;
    copy?: string;
    images?: string[];
  };
}

const DEFAULT_IMAGES = [
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=80'
];

export const BrandAbout: React.FC<BrandAboutProps> = ({ data }) => {
  const heading = data?.heading || 'Golden Tower: Airport Residential';
  const copy = data?.copy || "Golden Tower Ghana is a luxurious residence in Accra’s most prestigious address, Airport Residential. The development stands tall at 18 floors, delivering unparalleled views of prime Accra. Escape into our superior and expansive amenities that elevate your living even further. Choose a home that delivers on all facets of an investment. Welcome to Golden Tower, Accra’s most prestigious residence.";
  const rawImages = data?.images && data.images.length > 0 ? data.images : DEFAULT_IMAGES;
  const images = rawImages.filter(Boolean);

  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const prevSlide = () => {
    setCurrentIdx((prev) => (prev - 1 + images.length) % images.length);
  };

  const nextSlide = () => {
    setCurrentIdx((prev) => (prev + 1) % images.length);
  };

  return (
    <section id="about" className="relative py-28 md:py-36 bg-[#0a0a0f] text-white overflow-hidden border-t border-white/5">
      {/* Subtle ambient light */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#C5A46D]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Heading & Copy */}
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#C5A46D]/10 border border-[#C5A46D]/30 text-[#C5A46D] text-xs uppercase tracking-[0.25em] font-medium">
              About Golden Tower
            </div>

            <h2 className="text-3xl md:text-5xl font-light leading-tight text-white">
              {heading.includes(':') ? (
                <>
                  <span className="font-extralight">{heading.split(':')[0]}:</span>{' '}
                  <span className="font-semibold text-[#C5A46D]">{heading.split(':')[1]}</span>
                </>
              ) : (
                heading
              )}
            </h2>

            <div className="w-16 h-0.5 bg-gradient-to-r from-[#C5A46D] to-transparent" />

            <div className="text-gray-300 text-base md:text-lg leading-relaxed space-y-4 font-light">
              {copy.split('\n').filter(Boolean).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Quick Highlights */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                <span className="text-2xl md:text-3xl font-light text-[#C5A46D]">18</span>
                <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Floors</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                <span className="text-xl md:text-2xl font-light text-[#C5A46D]">Accra</span>
                <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Airport Res.</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                <span className="text-xl md:text-2xl font-light text-[#C5A46D]">Luxury</span>
                <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Living</p>
              </div>
            </div>
          </div>

          {/* Right Column: Photo Slideshow */}
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#121218] group aspect-[4/3] md:aspect-[16/11]">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                    idx === currentIdx ? 'opacity-100 scale-100' : 'opacity-0 scale-105 pointer-events-none'
                  }`}
                  style={{ transition: 'opacity 1s ease-in-out, transform 1.2s ease-in-out' }}
                >
                  <img
                    src={img}
                    alt={`Golden Tower - Slide ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
                </div>
              ))}

              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    aria-label="Previous slide"
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-[#C5A46D] text-white hover:text-black border border-white/20 hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 opacity-80 hover:opacity-100 backdrop-blur-sm cursor-pointer z-20"
                  >
                    <i className="ri-arrow-left-s-line text-2xl"></i>
                  </button>
                  <button
                    onClick={nextSlide}
                    aria-label="Next slide"
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-[#C5A46D] text-white hover:text-black border border-white/20 hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 opacity-80 hover:opacity-100 backdrop-blur-sm cursor-pointer z-20"
                  >
                    <i className="ri-arrow-right-s-line text-2xl"></i>
                  </button>

                  {/* Dot Indicators */}
                  <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20 bg-black/40 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/10">
                    {images.map((_, dotIdx) => (
                      <button
                        key={dotIdx}
                        onClick={() => setCurrentIdx(dotIdx)}
                        aria-label={`Go to slide ${dotIdx + 1}`}
                        className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                          dotIdx === currentIdx
                            ? 'w-7 bg-[#C5A46D]'
                            : 'w-2 bg-white/40 hover:bg-white/70'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default BrandAbout;
